import React, { useState, useEffect } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'
import WalletQRCode from './WalletQRCode'

// API base URL for server-side endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// Users allowed to access this feature (for testing)
const ALLOWED_USER_IDS = [106]

// Card pricing
const STANDARD_CARD_PRICE = 100
const NAMED_CARD_PRICE = 120
const DEFAULT_INTERNATIONAL_SHIPPING = 200

// Custom name validation
const MAX_NAME_LENGTH = 26
const NAME_REGEX = /^[a-zA-Z\s\-'\.]+$/

interface ShippingRate {
  id: number
  country_name: string
  country_code: string
  shipping_cost_usd: number
  is_active: boolean
}

interface VisaCardPurchaseProps {
  userId: number
  onSuccess?: () => void
}

export const VisaCardPurchase: React.FC<VisaCardPurchaseProps> = ({
  userId,
  onSuccess
}) => {
  // Access control
  if (!ALLOWED_USER_IDS.includes(userId)) {
    return null
  }

  const [isExpanded, setIsExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isEligible, setIsEligible] = useState(false)
  const [totalShares, setTotalShares] = useState(0)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Card options
  const [cardType, setCardType] = useState<'standard' | 'named'>('standard')
  const [customName, setCustomName] = useState('')
  const [nameError, setNameError] = useState('')

  // Shipping
  const [shippingRates, setShippingRates] = useState<ShippingRate[]>([])
  const [selectedCountry, setSelectedCountry] = useState('')
  const [deliveryAddress, setDeliveryAddress] = useState('')
  const [shippingCost, setShippingCost] = useState(DEFAULT_INTERNATIONAL_SHIPPING)

  // Payment
  const [paymentMethod, setPaymentMethod] = useState<'crypto' | 'bank_transfer'>('crypto')
  const [showPaymentStep, setShowPaymentStep] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Crypto payment fields
  const [transactionHash, setTransactionHash] = useState('')
  const [senderWallet, setSenderWallet] = useState('')
  const [cryptoNetwork, setCryptoNetwork] = useState<any>(null)
  const [cryptoWallets, setCryptoWallets] = useState<any[]>([])

  // Bank transfer fields
  const [proofFile, setProofFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState('')

  // Calculate totals
  const cardPrice = cardType === 'standard' ? STANDARD_CARD_PRICE : NAMED_CARD_PRICE
  const totalAmount = cardPrice + shippingCost

  // Check eligibility on mount
  useEffect(() => {
    checkEligibility()
    loadShippingRates()
    loadCryptoWallets()
  }, [userId])

  // Update shipping cost when country changes
  useEffect(() => {
    if (selectedCountry) {
      const rate = shippingRates.find(r => r.country_name === selectedCountry)
      setShippingCost(rate?.shipping_cost_usd ?? DEFAULT_INTERNATIONAL_SHIPPING)
    }
  }, [selectedCountry, shippingRates])

  const checkEligibility = async () => {
    try {
      setIsLoading(true)

      const response = await fetch(`${API_BASE_URL}/api/user/visa-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'check_share_eligibility'
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      const total = result.data?.totalShares || 0
      setTotalShares(total)
      setIsEligible(total >= 1)
    } catch (err: any) {
      console.error('Error checking eligibility:', err)
      setError('Failed to check eligibility')
    } finally {
      setIsLoading(false)
    }
  }

  const loadShippingRates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/visa-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'load_shipping_rates'
        })
      })

      const result = await response.json()
      if (response.ok && result.data?.rates) {
        setShippingRates(result.data.rates)
      }
    } catch (err: any) {
      console.error('Error loading shipping rates:', err)
    }
  }

  const loadCryptoWallets = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/visa-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'load_crypto_wallets'
        })
      })

      const result = await response.json()
      if (response.ok && result.data?.wallets) {
        setCryptoWallets(result.data.wallets)
        if (result.data.wallets.length > 0) {
          setCryptoNetwork(result.data.wallets[0])
        }
      }
    } catch (err: any) {
      console.error('Error loading crypto wallets:', err)
    }
  }

  const validateCustomName = (name: string): boolean => {
    if (!name.trim()) {
      setNameError('Name is required for named cards')
      return false
    }
    if (name.length > MAX_NAME_LENGTH) {
      setNameError(`Name must be ${MAX_NAME_LENGTH} characters or less`)
      return false
    }
    if (!NAME_REGEX.test(name)) {
      setNameError('Name can only contain letters, spaces, hyphens, apostrophes, and periods')
      return false
    }
    setNameError('')
    return true
  }

  const handleProceedToPayment = () => {
    setError('')

    // Validate form
    if (!selectedCountry) {
      setError('Please select a delivery country')
      return
    }
    if (!deliveryAddress.trim()) {
      setError('Please enter your delivery address')
      return
    }
    if (cardType === 'named' && !validateCustomName(customName)) {
      return
    }

    setShowConfirmation(true)
  }

  const handleConfirmOrder = () => {
    setShowConfirmation(false)
    setShowPaymentStep(true)
  }

  const handleSubmitCryptoPayment = async () => {
    if (!transactionHash.trim()) {
      setError('Please enter the transaction hash')
      return
    }
    if (!senderWallet.trim()) {
      setError('Please enter your sender wallet address')
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/visa-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'submit_purchase_order',
          data: {
            cardType,
            customName: cardType === 'named' ? customName.trim().toUpperCase() : null,
            shippingCountry: selectedCountry,
            shippingAddress: deliveryAddress.trim(),
            cardPrice,
            shippingCost,
            totalPrice: totalAmount,
            paymentMethod: 'crypto',
            transactionHash: transactionHash.replace(/\s+/g, '')
          }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      setSuccess('Your VISA card order has been submitted! We will verify your payment and process your order.')
      setShowPaymentStep(false)
      resetForm()
      onSuccess?.()
    } catch (err: any) {
      console.error('Error submitting order:', err)
      setError(err.message || 'Failed to submit order')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmitBankPayment = async () => {
    if (!proofFile) {
      setError('Please upload proof of payment')
      return
    }

    setIsSubmitting(true)
    setError('')
    setUploadProgress('Preparing upload...')

    try {
      // Convert file to base64
      const reader = new FileReader()
      const proofFileBase64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(proofFile)
      })

      setUploadProgress('Submitting order...')

      const response = await fetch(`${API_BASE_URL}/api/user/visa-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'submit_purchase_order',
          data: {
            cardType,
            customName: cardType === 'named' ? customName.trim().toUpperCase() : null,
            shippingCountry: selectedCountry,
            shippingAddress: deliveryAddress.trim(),
            cardPrice,
            shippingCost,
            totalPrice: totalAmount,
            paymentMethod: 'bank',
            proofFileBase64,
            proofFileName: proofFile.name,
            proofFileType: proofFile.type
          }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      setSuccess('Your VISA card order has been submitted! We will verify your payment and process your order.')
      setShowPaymentStep(false)
      resetForm()
      onSuccess?.()
    } catch (err: any) {
      console.error('Error submitting order:', err)
      setError(err.message || 'Failed to submit order')
    } finally {
      setIsSubmitting(false)
      setUploadProgress('')
    }
  }

  const resetForm = () => {
    setCardType('standard')
    setCustomName('')
    setSelectedCountry('')
    setDeliveryAddress('')
    setPaymentMethod('crypto')
    setTransactionHash('')
    setSenderWallet('')
    setProofFile(null)
  }

  // Collapsed button view
  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-4 px-6 rounded-lg flex items-center justify-between transition-all shadow-lg"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">💳</span>
          <div className="text-left">
            <div className="font-bold">Aureus VISA Card</div>
            <div className="text-sm text-blue-200">Order your physical VISA card</div>
          </div>
        </div>
        <span className="text-xl">→</span>
      </button>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg border border-blue-500/50 p-6">
        <div className="flex items-center justify-center gap-3">
          <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full" />
          <span className="text-gray-300">Checking eligibility...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg border border-blue-500/50 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="text-3xl">💳</span>
          <div>
            <h3 className="text-xl font-bold text-blue-400">Aureus VISA Card</h3>
            <p className="text-sm text-white">Physical debit card for your account</p>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(false)}
          className="text-gray-400 hover:text-white text-2xl"
        >
          ✕
        </button>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-900/30 border border-green-500/50 rounded-lg">
          <p className="text-green-400 flex items-center gap-2">
            <span>✅</span> {success}
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-900/30 border border-red-500/50 rounded-lg">
          <p className="text-red-400 flex items-center gap-2">
            <span>❌</span> {error}
          </p>
        </div>
      )}

      {/* Eligibility Check */}
      {!isEligible ? (
        <div className="text-center py-8">
          <div className="text-6xl mb-4">🔒</div>
          <h4 className="text-xl font-semibold text-amber-400 mb-2">Share Ownership Required</h4>
          <p className="text-white mb-4">
            You need to own at least 1 approved share to order an Aureus VISA Card.
          </p>
          <p className="text-sm text-gray-500">
            Your current shares: <span className="text-amber-400 font-bold">{totalShares}</span>
          </p>
        </div>
      ) : !showPaymentStep ? (
        <>
          {/* Card Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-3">Select Card Type</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setCardType('standard')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  cardType === 'standard'
                    ? 'border-blue-500 bg-blue-900/30'
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                <div className="text-3xl mb-2">💳</div>
                <div className="font-semibold text-white">Standard Card</div>
                <div className="text-sm text-white">No custom name</div>
                <div className="text-xl font-bold text-green-400 mt-2">${STANDARD_CARD_PRICE}</div>
              </button>
              <button
                onClick={() => setCardType('named')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  cardType === 'named'
                    ? 'border-blue-500 bg-blue-900/30'
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                <div className="text-3xl mb-2">✨</div>
                <div className="font-semibold text-white">Named Card</div>
                <div className="text-sm text-white">Your name embossed</div>
                <div className="text-xl font-bold text-green-400 mt-2">${NAMED_CARD_PRICE}</div>
              </button>
            </div>
          </div>

          {/* Custom Name Input (for named cards) */}
          {cardType === 'named' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Name to Emboss on Card
              </label>
              <input
                type="text"
                value={customName}
                onChange={(e) => {
                  setCustomName(e.target.value.toUpperCase())
                  if (nameError) validateCustomName(e.target.value)
                }}
                maxLength={MAX_NAME_LENGTH}
                placeholder="JOHN DOE"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white uppercase focus:border-blue-500 focus:outline-none"
              />
              <div className="flex justify-between mt-1">
                {nameError ? (
                  <span className="text-red-400 text-sm">{nameError}</span>
                ) : (
                  <span className="text-gray-500 text-sm">Letters, spaces, and hyphens only</span>
                )}
                <span className="text-gray-500 text-sm">{customName.length}/{MAX_NAME_LENGTH}</span>
              </div>
            </div>
          )}

          {/* Delivery Country */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Delivery Country
            </label>
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
            >
              <option value="">Select your country...</option>
              {shippingRates.map((rate) => (
                <option key={rate.id} value={rate.country_name}>
                  {rate.country_name} {rate.shipping_cost_usd === 0 ? '(FREE Shipping)' : `($${rate.shipping_cost_usd} shipping)`}
                </option>
              ))}
            </select>
          </div>

          {/* Delivery Address */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Full Delivery Address
            </label>
            <textarea
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              placeholder="Street address, City, State/Province, Postal Code"
              rows={3}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none resize-none"
            />
          </div>

          {/* Payment Method */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-3">Payment Method</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setPaymentMethod('crypto')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  paymentMethod === 'crypto'
                    ? 'border-purple-500 bg-purple-900/30'
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                <div className="text-2xl mb-1">₿</div>
                <div className="font-medium text-white">Cryptocurrency</div>
              </button>
              <button
                onClick={() => setPaymentMethod('bank_transfer')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  paymentMethod === 'bank_transfer'
                    ? 'border-green-500 bg-green-900/30'
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                <div className="text-2xl mb-1">🏦</div>
                <div className="font-medium text-white">Bank Transfer</div>
              </button>
            </div>
          </div>

          {/* Price Summary */}
          <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-white mb-3">Order Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white">{cardType === 'standard' ? 'Standard' : 'Named'} Card</span>
                <span className="text-white">${cardPrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Shipping ({selectedCountry || 'Select country'})</span>
                <span className={shippingCost === 0 ? 'text-green-400' : 'text-white'}>
                  {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                </span>
              </div>
              <div className="border-t border-gray-700 pt-2 flex justify-between">
                <span className="font-semibold text-white">Total</span>
                <span className="font-bold text-green-400 text-lg">${totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Proceed Button */}
          <button
            onClick={handleProceedToPayment}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition-all"
          >
            Proceed to Payment
          </button>
        </>
      ) : (
        /* Payment Step */
        <div>
          <button
            onClick={() => setShowPaymentStep(false)}
            className="text-blue-400 hover:text-blue-300 mb-4 flex items-center gap-2"
          >
            ← Back to order details
          </button>

          <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-white mb-2">Payment Required</h4>
            <div className="text-3xl font-bold text-green-400">${totalAmount.toFixed(2)} USD</div>
          </div>

          {paymentMethod === 'crypto' ? (
            <div className="space-y-4">
              {/* Network Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Select Network</label>
                <div className="grid grid-cols-2 gap-2">
                  {cryptoWallets.map((wallet) => (
                    <button
                      key={wallet.id}
                      onClick={() => setCryptoNetwork(wallet)}
                      className={`p-3 rounded-lg border-2 text-sm ${
                        cryptoNetwork?.id === wallet.id
                          ? 'border-purple-500 bg-purple-900/30'
                          : 'border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      {wallet.network_name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Wallet Address */}
              {cryptoNetwork && (
                <div className="bg-gray-900 p-4 rounded-lg">
                  <p className="text-sm text-white mb-2">Send ${totalAmount.toFixed(2)} USDT to:</p>
                  <WalletQRCode walletAddress={cryptoNetwork.wallet_address} size={180} amount={totalAmount} />
                  <code className="text-green-400 text-sm break-all block mb-2">
                    {cryptoNetwork.wallet_address}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(cryptoNetwork.wallet_address)}
                    className="text-blue-400 text-sm hover:text-blue-300"
                  >
                    📋 Copy Address
                  </button>
                </div>
              )}

              {/* Transaction Hash */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Transaction Hash
                </label>
                <input
                  type="text"
                  value={transactionHash}
                  onChange={(e) => setTransactionHash(e.target.value)}
                  placeholder="Enter transaction hash..."
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* Sender Wallet */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Your Wallet Address
                </label>
                <input
                  type="text"
                  value={senderWallet}
                  onChange={(e) => setSenderWallet(e.target.value)}
                  placeholder="Enter your wallet address..."
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                />
              </div>

              <button
                onClick={handleSubmitCryptoPayment}
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition-all disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Payment'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-amber-900/30 border border-amber-500/50 rounded-lg p-4">
                <p className="text-amber-400 text-sm">
                  Please make a bank transfer of <strong>${totalAmount.toFixed(2)} USD</strong> and upload your proof of payment below.
                </p>
              </div>

              {/* Proof Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Proof of Payment
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-blue-600 file:text-white"
                />
                {proofFile && (
                  <p className="text-green-400 text-sm mt-2">✓ {proofFile.name}</p>
                )}
              </div>

              {uploadProgress && (
                <p className="text-blue-400 text-sm">{uploadProgress}</p>
              )}

              <button
                onClick={handleSubmitBankPayment}
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-semibold py-3 px-6 rounded-lg transition-all disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Payment'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-md w-full p-6 border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">Confirm Your Order</h3>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-white">Card Type:</span>
                <span className="text-white">{cardType === 'standard' ? 'Standard' : 'Named'}</span>
              </div>
              {cardType === 'named' && (
                <div className="flex justify-between">
                  <span className="text-white">Name on Card:</span>
                  <span className="text-white">{customName}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-white">Delivery To:</span>
                <span className="text-white">{selectedCountry}</span>
              </div>
              <div className="border-t border-gray-700 pt-3">
                <div className="flex justify-between">
                  <span className="text-white">Card Price:</span>
                  <span className="text-white">${cardPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white">Shipping:</span>
                  <span className={shippingCost === 0 ? 'text-green-400' : 'text-white'}>
                    {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-lg mt-2">
                  <span className="text-white">Total:</span>
                  <span className="text-green-400">${totalAmount.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Payment Method:</span>
                <span className="text-white">{paymentMethod === 'crypto' ? 'Cryptocurrency' : 'Bank Transfer'}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmOrder}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg"
              >
                Confirm Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
